import { useState, useEffect, useCallback } from "react";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const COLORS = ["#5b6ef8","#22d3a0","#f5a623","#a855f7","#ef4444"];

/* ── Haversine côté client (fallback si pas de coords serveur) ── */
function haversine(lat1,lng1,lat2,lng2){
  const R=6371, dLat=(lat2-lat1)*Math.PI/180, dLng=(lng2-lng1)*Math.PI/180;
  const a=Math.sin(dLat/2)**2+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return +(R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a))).toFixed(1);
}

export default function WarningsPanel({ T, onSelectInt, onEscalade }) {
  const [warnings,  setWarnings]  = useState([]);
  const [techPos,   setTechPos]   = useState([]);
  const [smartReco, setSmartReco] = useState({}); // { [inter_id]: { tech, loading } }
  const [dismissed, setDismissed] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const [w,p] = await Promise.all([
        fetch(`${BASE}/interventions/warnings`,{headers:h()}).then(r=>r.json()).catch(()=>[]),
        fetch(`${BASE}/interventions/tech-positions`,{headers:h()}).then(r=>r.json()).catch(()=>[]),
      ]);
      setWarnings(Array.isArray(w)?w:[]);
      setTechPos(Array.isArray(p)?p:[]);
    } catch{}
  },[]);

  useEffect(()=>{ load(); const t=setInterval(load,30000); return()=>clearInterval(t); },[load]);

  /* ── Smart assign pour chaque warning ── */
  useEffect(()=>{
    warnings.forEach(async w => {
      if(smartReco[w.id]||dismissed.has(w.id)) return;
      const lat = w.map_lat||w.client_lat;
      const lng = w.map_lng||w.client_lng;
      if(!lat||!lng) return;
      setSmartReco(p=>({...p,[w.id]:{loading:true,tech:null}}));
      try {
        const urgence = w.priorite==="urgente"?"urgente":w.priorite==="haute"?"haute":"normale";
        const res = await fetch(
          `${BASE}/interventions/smart-assign?lat=${lat}&lng=${lng}&urgence=${urgence}`,
          {headers:h()}
        ).then(r=>r.json());
        const best = Array.isArray(res)&&res[0];
        setSmartReco(p=>({...p,[w.id]:{loading:false,tech:best||null}}));
      } catch{
        setSmartReco(p=>({...p,[w.id]:{loading:false,tech:null}}));
      }
    });
  },[warnings]);

  const triggerEscalade = (w, tech) => {
    onEscalade&&onEscalade({
      id: `esc-${w.id}`,
      interId: w.id,
      titre: `Retard sur "${w.titre}"`,
      message: `${w.technicien_nom} accuse +${Math.round(parseFloat(w.retard_min))} min de retard. ${tech.prenom} ${tech.nom} est à ${tech.distance_km} km et disponible. Confirmer la réassignation ?`,
      tech: { ...tech, raison: tech.raison },
      onConfirm: () => reassign(w.id, tech.user_id, w),
      onModify:  () => onSelectInt&&onSelectInt(w),
    });
  };

  const reassign = async (interId, techId, w) => {
    try {
      await fetch(`${BASE}/interventions/${interId}`,{
        method:"PATCH", headers:{...h(),"Content-Type":"application/json"},
        body:JSON.stringify({technicien_id:techId, statut:"planifiee"}),
      });
      setDismissed(d=>new Set([...d,interId]));
      load();
    } catch{}
  };

  const activeWarnings = warnings.filter(w=>!dismissed.has(w.id));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* ── Warnings retards ── */}
      {activeWarnings.length>0&&(
        <div style={{background:T.card,border:`2px solid ${T.danger}40`,borderRadius:14,overflow:"hidden"}}>
          <div style={{padding:"12px 18px",background:`${T.danger}15`,borderBottom:`1px solid ${T.danger}30`,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>🚨</span>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:800,color:T.danger}}>Interventions en retard</div>
              <div style={{fontSize:11,color:T.muted}}>{activeWarnings.length} technicien(s) en dépassement</div>
            </div>
            <button onClick={load} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:12}}>↻ Actualiser</button>
          </div>

          {activeWarnings.map(w=>{
            const retard = Math.round(parseFloat(w.retard_min));
            const reco   = smartReco[w.id];
            const tech   = reco?.tech;

            return(
              <div key={w.id} style={{borderBottom:`1px solid ${T.border}22`}}>

                {/* Ligne intervention */}
                <div onClick={()=>onSelectInt&&onSelectInt(w)}
                  style={{padding:"12px 18px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.dangerDim}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <div style={{width:42,height:42,borderRadius:"50%",background:T.dangerDim,border:`2px solid ${T.danger}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:T.danger,flexShrink:0}}>
                    +{retard}m
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.titre}</div>
                    <div style={{fontSize:11,color:T.muted}}>🔧 {w.technicien_nom||"—"} · 👤 {w.client_nom||"—"}</div>
                    <div style={{fontSize:11,color:T.danger,fontWeight:600}}>⏱ {Math.round(parseFloat(w.duree_actuelle_min))} min / {w.duree_estimee_min} min prévues</div>
                  </div>
                  <div style={{flexShrink:0,textAlign:"right"}}>
                    <div style={{fontSize:11,color:T.danger,fontWeight:700,background:T.dangerDim,padding:"3px 9px",borderRadius:20}}>⚠ +{retard} min</div>
                    <button onClick={e=>{e.stopPropagation();window.open(`tel:${w.client_telephone}`);}}
                      style={{marginTop:5,fontSize:10,color:T.success,background:`${T.success}15`,border:`1px solid ${T.success}30`,borderRadius:6,padding:"2px 8px",cursor:"pointer"}}>
                      📞 Prévenir client
                    </button>
                  </div>
                </div>

                {/* Suggestion IA */}
                {reco?.loading&&(
                  <div style={{padding:"8px 18px 10px",fontSize:11,color:T.muted}}>
                    <span style={{opacity:0.6}}>⚡ Calcul du meilleur technicien disponible…</span>
                  </div>
                )}
                {!reco?.loading&&tech&&(
                  <div style={{margin:"0 18px 12px",padding:"10px 12px",background:`${T.accent}08`,border:`1px solid ${T.accent}25`,borderRadius:10,display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:32,height:32,borderRadius:"50%",background:`${T.accent}20`,border:`2px solid ${T.accent}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:T.accent,flexShrink:0}}>
                      {(tech.prenom||"?")[0]}{(tech.nom||"?")[0]}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.accent}}>💡 Suggestion : {tech.prenom} {tech.nom}</div>
                      <div style={{fontSize:11,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tech.raison}</div>
                    </div>
                    <button
                      onClick={e=>{e.stopPropagation();triggerEscalade(w,tech);}}
                      style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.accent}`,background:T.accentDim,color:T.accent,fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                      Réassigner ⚡
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Positions techniciens ── */}
      {techPos.length>0&&(
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
          <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>📡</span>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:T.text}}>Techniciens actifs</div>
              <div style={{fontSize:11,color:T.muted}}>Positions mises à jour en temps réel</div>
            </div>
          </div>
          {techPos.map((p,pi)=>{
            const c   = COLORS[pi%COLORS.length];
            const age = Math.round((Date.now()-new Date(p.updated_at).getTime())/60000);
            return(
              <div key={p.user_id} style={{padding:"11px 18px",borderBottom:`1px solid ${T.border}22`,display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:38,height:38,borderRadius:"50%",background:`${c}22`,border:`2px solid ${c}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:c,flexShrink:0}}>
                  {p.prenom[0]}{p.nom[0]}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{p.prenom} {p.nom}</div>
                  {p.inter_titre&&<div style={{fontSize:11,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🔧 {p.inter_titre}</div>}
                  {p.client_nom&&<div style={{fontSize:11,color:T.muted}}>👤 {p.client_nom}</div>}
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:10,color:age<5?T.success:age<30?T.warning:T.muted,fontWeight:600}}>
                    {age<1?"À l'instant":age+"min"}
                  </div>
                  <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer"
                    style={{fontSize:10,color:T.accent,textDecoration:"none",fontWeight:600}}>📍 Voir</a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeWarnings.length===0&&techPos.length===0&&(
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:20,textAlign:"center",color:T.muted}}>
          <div style={{fontSize:32,marginBottom:8}}>✅</div>
          <div style={{fontSize:13,fontWeight:600}}>Tout est sous contrôle</div>
          <div style={{fontSize:11,marginTop:3}}>Aucun retard détecté · Aucun technicien actif</div>
        </div>
      )}
    </div>
  );
}
